import { useState, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { MemberCard } from "@/components/MemberCard";
import { FilterChips } from "@/components/FilterChips";
import { Button } from "@/components/Button";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";
import { Icon } from "@/components/Icon";

// JTBD: "use it when assigning work" — so filter by current load (who's free
// vs overloaded), not the placeholder Leads/Operations/Volunteers chips that
// matched no real data (every member's instance role is admin/member).
const LOAD_FILTERS = [
  { value: "all", label: "All" },
  { value: "light", label: "Available" },
  { value: "balanced", label: "Balanced" },
  { value: "heavy", label: "Overloaded" },
] as const;

type LoadFilter = (typeof LOAD_FILTERS)[number]["value"];

const SORTS = [
  { value: "name", label: "Name (A–Z)" },
  { value: "open", label: "Most open tasks" },
  { value: "load", label: "Heaviest load" },
] as const;
type Sort = (typeof SORTS)[number]["value"];
const LOAD_RANK: Record<string, number> = { heavy: 3, balanced: 2, light: 1 };

/**
 * S10 Team — designs/Team.html
 * pg-head with inline count pill, pg-sub caption, filter chips + search,
 * MemberCard grid.
 */
function TeamPage() {
  const [filter, setFilter] = useState<LoadFilter>("all");
  const [sort, setSort] = useState<Sort>("name");
  const [search, setSearch] = useState("");

  const team = useQuery({
    queryKey: queryKeys.team(),
    queryFn: api.listTeam,
  });
  const workload = useQuery({
    queryKey: queryKeys.teamWorkload(),
    queryFn: api.teamWorkload,
  });

  type WorkloadMember = {
    userId: string;
    name: string;
    openCount: number;
    completedCount: number;
    projectCount: number;
    load: "light" | "balanced" | "heavy";
  };

  const workloadMap = useMemo(() => {
    const m = new Map<string, WorkloadMember>();
    for (const w of workload.data?.members ?? []) m.set(w.userId, w);
    return m;
  }, [workload.data]);

  const rawMembers: Array<{
    id: string;
    email: string;
    name?: string | null;
    role?: string | null;
  }> = team.data?.members ?? [];

  const items = rawMembers
    .map((u) => {
      const wl = workloadMap.get(u.id);
      return {
        id: u.id,
        name: u.name ?? u.email,
        email: u.email,
        role: u.role ?? "Member",
        online: false as boolean,
        openCount: wl?.openCount,
        completedCount: wl?.completedCount,
        projectCount: wl?.projectCount,
        load: wl?.load,
        projects: [] as { id: string; color: string }[],
      };
    })
    .filter((m) => {
      const loadMatches = filter === "all" || m.load === filter;
      const searchMatches =
        !search ||
        m.name.toLowerCase().includes(search.toLowerCase()) ||
        m.email.toLowerCase().includes(search.toLowerCase());
      return loadMatches && searchMatches;
    })
    .sort((a, b) => {
      if (sort === "open") return (b.openCount ?? 0) - (a.openCount ?? 0);
      if (sort === "load")
        return (LOAD_RANK[b.load ?? ""] ?? 0) - (LOAD_RANK[a.load ?? ""] ?? 0);
      return a.name.localeCompare(b.name);
    });

  return (
    <div className="pg-wrap">
      <header className="pg-head">
        <h1>Team</h1>
        <span className="count" aria-label={`${items.length} teammates`}>
          {items.length}
        </span>
        <div className="right">
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              window.location.href = "/admin/invites";
            }}
          >
            <Icon name="plus" size={14} /> Invite teammate
          </Button>
        </div>
      </header>
      <p className="pg-sub">
        Everyone at Because — their focus, current workload, and which projects
        they&apos;re driving. Use it when assigning work.
      </p>

      <div className="toolbar">
        <FilterChips
          options={LOAD_FILTERS}
          value={filter}
          onChange={setFilter}
          ariaLabel="Filter by workload"
        />
        <div className="spacer" />
        <select
          className="select"
          style={{ width: 160, height: 34 }}
          value={sort}
          onChange={(e) => setSort(e.target.value as Sort)}
          aria-label="Sort teammates"
        >
          {SORTS.map((s) => (
            <option key={s.value} value={s.value}>
              Sort: {s.label}
            </option>
          ))}
        </select>
        <div className="input-search" style={{ width: 240 }}>
          <Icon name="search" size={14} />
          <input
            type="search"
            className="input"
            placeholder="Search people…"
            aria-label="Search people"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {team.isLoading ? (
        <div className="team-grid" style={{ marginTop: 24 }}>
          <Skeleton count={6} height={200} />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon="users"
          title="Just you so far."
          caption="Invite teammates to start collaborating."
          actionLabel="Invite teammate"
          onAction={() => {
            window.location.href = "/admin/invites";
          }}
        />
      ) : (
        <div className="team-grid">
          {items.map((m) => (
            <MemberCard key={m.id} member={m} />
          ))}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/team/")({ component: TeamPage });
